import React, { useCallback, useMemo } from "react";
import {
  decideTPRMRiskAcceptanceApi,
  getTPRMRiskAcceptancesApi,
  revokeTPRMRiskAcceptanceApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import TPRMResourceTable, { StatusBadge } from "./TPRMResourceTable";
import {
  displayTPRMValue,
  formatTPRMDate,
} from "./tprmPageUtils";

const FILTERS = [
  {
    id: "status",
    definition: {
      variant: "select",
      label: "Decision status",
      placeholder: "All statuses",
      options: ["Pending", "Approved", "Rejected", "Expired", "Revoked"].map(
        (value) => ({ value, label: value }),
      ),
    },
  },
];

const DECISION_DIALOG = {
  title: "Decide risk acceptance",
  fields: [
    {
      name: "decision",
      label: "Decision",
      type: "select",
      required: true,
      defaultValue: "Approved",
      options: ["Approved", "Rejected"].map((value) => ({
        value,
        label: value,
      })),
    },
    {
      name: "reason",
      label: "Decision reason",
      type: "textarea",
      required: true,
    },
  ],
};

const REVOKE_DIALOG = {
  title: "Revoke risk acceptance",
  description: "Provide an optional reason for revoking this acceptance.",
  fields: [{ name: "reason", label: "Reason", type: "textarea" }],
};

const RiskAcceptancePage = () => {
  const fetchPage = useCallback((params) => getTPRMRiskAcceptancesApi(params), []);
  const columns = useMemo(
    () => [
      {
        accessorKey: "acceptance_id",
        header: "Risk acceptance ID",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "vendor",
        header: "Vendor reference",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "finding",
        header: "Related finding",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "requested_by",
        header: "Requested by",
        size: 155,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "requested_until",
        header: "Expiry date",
        size: 145,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        accessorKey: "status",
        header: "Decision status",
        size: 150,
        cell: ({ getValue }) => <StatusBadge value={getValue()} />,
      },
    ],
    [],
  );

  const getActions = useCallback((acceptance, { canApprove }) => {
    if (!canApprove) return [];
    if (acceptance.status === "Pending") {
      return [
        {
          action: "decide",
          label: "Decide",
          dialog: DECISION_DIALOG,
          initialValues: { decision: "Approved" },
        },
      ];
    }
    if (acceptance.status === "Approved") {
      return [
        {
          action: "revoke",
          label: "Revoke",
          variant: "danger",
          dialog: REVOKE_DIALOG,
        },
      ];
    }
    return [];
  }, []);

  const performAction = useCallback((acceptance, item, values) => {
    if (item.action === "decide") {
      return decideTPRMRiskAcceptanceApi(acceptance.acceptance_id, {
        decision: values.decision,
        reason: values.reason,
      });
    }
    if (item.action === "revoke") {
      return revokeTPRMRiskAcceptanceApi(acceptance.acceptance_id, {
        reason: values.reason,
      });
    }
    throw new Error("Unsupported risk-acceptance action.");
  }, []);

  return (
    <TPRMResourceTable
      columns={columns}
      description="Review risk-acceptance requests, decisions, and expiry dates."
      fetchPage={fetchPage}
      filters={FILTERS}
      getActions={getActions}
      getRowId={(acceptance) => acceptance.acceptance_id}
      performAction={performAction}
      title="Risk Acceptance"
    />
  );
};

export default RiskAcceptancePage;
